import { createContext, useContext, useEffect, useSyncExternalStore } from "react";

/**
 * The shell layout contract: everything a page may know about the chrome
 * around it, in one module that names no shell (NO-751).
 *
 * Pages that move with the chrome (the Appointment workspace's docked drawers
 * and mobile sheet, the session detail split) read their timing, breakpoints
 * and top-bar geometry here and ask for a squeeze here. Whichever shell is
 * mounted provides the squeeze and, when its top bar differs, the geometry, so
 * a second shell can wrap the same pages without the pages changing.
 */

// Shared timing so the siderail width, drawer slide, and scrim fade move
// together. Every workspace transition (the Previous SOAP notes drawer, the
// accordion, tab swaps) reads the same constant so the shell moves as one.
export const RAIL_TRANSITION =
  "duration-[230ms] ease-[cubic-bezier(0.2,0.7,0.3,1)] motion-reduce:transition-none";

// The lg breakpoint, tracked in JS so drawer-only behavior (inert, focus,
// auto-close on resize, which side panel opens on load) follows the exact same
// boundary as the CSS. Shell and pages read this one query, so they never
// disagree about what "desktop" means.
export const DESKTOP_QUERY = "(min-width: 1024px)";

// Between 1024 and 1279px the canvas cannot hold the expanded siderail, the
// 720px column, and the docked Previous SOAP notes drawer at once. A workspace
// that opens its drawer asks the shell to squeeze the siderail to the icon
// strip for as long as the drawer is open; the RD's own width choice is
// untouched and comes back when the drawer closes.
export const SQUEEZE_QUERY = "(max-width: 1279px)";

function subscribeToDesktop(onChange: () => void): () => void {
  const media = window.matchMedia(DESKTOP_QUERY);
  media.addEventListener("change", onChange);

  return () => media.removeEventListener("change", onChange);
}

export function useIsDesktop(): boolean {
  return useSyncExternalStore(
    subscribeToDesktop,
    () => window.matchMedia(DESKTOP_QUERY).matches
  );
}

function subscribeToSqueeze(onChange: () => void): () => void {
  const media = window.matchMedia(SQUEEZE_QUERY);
  media.addEventListener("change", onChange);

  return () => media.removeEventListener("change", onChange);
}

export function useIsSqueezeWidth(): boolean {
  return useSyncExternalStore(
    subscribeToSqueeze,
    () => window.matchMedia(SQUEEZE_QUERY).matches
  );
}

const ShellSqueezeContext = createContext<(active: boolean) => void>(() => {});

/** Mounted by a shell around its canvas: receives each surface's squeeze request. */
export const ShellSqueezeProvider = ShellSqueezeContext.Provider;

/** Declares that this surface's docked drawer is open; the shell squeezes the siderail when the viewport needs it. */
export function useShellSiderailSqueeze(active: boolean): void {
  const setSqueezed = useContext(ShellSqueezeContext);
  useEffect(() => {
    setSqueezed(active);

    return () => setSqueezed(false);
  }, [active, setSqueezed]);
}

/**
 * Where the shell's top bar ends, as Tailwind classes spelled out in full so
 * the build sees them. Overlays (the siderail drawer, its scrim, a page's
 * Sheet) open under the bar rather than over it, and a page that fills the
 * viewport takes the height the bar leaves.
 */
export interface ShellTopBarGeometry {
  /** Fixed chrome (drawer, scrim) starts under the bar. */
  fixedTop: string;
  /** Overrides a Sheet's own top and height so it opens under the bar. */
  sheetFrame: string;
  /** A definite height for a page that fills the viewport under the bar. */
  pageFrame: string;
}

// The one top bar both of today's shells mount (shell-header.tsx). HEIGHT IS A
// CONSTANT, NOT A CLASS on that bar, because everything positioned from its
// bottom edge reads the numbers below; the two drifting apart is what once
// made the chrome jump between shells.
export const SHELL_HEADER_HEIGHT = "h-[65px]";

export const SHELL_HEADER_GEOMETRY: ShellTopBarGeometry = {
  fixedTop: "top-[65px]",
  sheetFrame: "!top-[65px] !h-[calc(100%-65px)]",
  pageFrame: "h-[calc(100vh-65px)]",
};

const ShellGeometryContext = createContext<ShellTopBarGeometry>(SHELL_HEADER_GEOMETRY);

/**
 * Mounted by a shell whose top bar is not the 65px ShellHeader. Both of
 * today's shells mount ShellHeader, so they rely on the default.
 */
export const ShellGeometryProvider = ShellGeometryContext.Provider;

export function useShellGeometry(): ShellTopBarGeometry {
  return useContext(ShellGeometryContext);
}

// The single-rail Always AI shell (NO-754, always-shell.tsx) has no 65px bar.
// On desktop the rail runs full height beside the canvas, so overlays start at
// the top. Below lg a 56px bar (`h-14`) carries the drawer toggle, so overlays
// start under it. Pages fill the canvas's own scroll region, which already has
// a definite height, so `pageFrame` needs no viewport math.
export const RAIL_SHELL_GEOMETRY: ShellTopBarGeometry = {
  fixedTop: "top-14 lg:top-0",
  sheetFrame: "!top-14 !h-[calc(100%-3.5rem)] lg:!top-0 lg:!h-full",
  pageFrame: "h-full",
};

// The rail shell's crumb row: a fixed height so a page whose crumbs arrive late
// (or never) does not move the canvas below it.
export const SHELL_CRUMB_ROW = "h-11 shrink-0";

/** Which frame wraps the page: today's chrome or the single rail. */
export type ShellFrame = "classic" | "rail";

const ShellFrameContext = createContext<ShellFrame>("classic");

/** Mounted by the rail shell. Classic shells rely on the default. */
export const ShellFrameProvider = ShellFrameContext.Provider;

export function useShellFrame(): ShellFrame {
  return useContext(ShellFrameContext);
}
